/**
 * Feedback response form — editorial "letter" treatment.
 *
 * Designed for the moment of giving upward feedback: warm off-white surface,
 * generous spacing, light tracking-tight question typography, type-rail on
 * each question card instead of badges/icons, segmented controls for
 * Likert / rating / yes-no. The chrome stays out of the way so the
 * respondent reads each question as a question, not a form field.
 *
 * Visual decisions documented inline next to the surfaces they apply to.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Send, Lock, ChevronDown } from 'lucide-react'
import type { FeedbackQuestion } from '@/services/api/feedbackService'
import { questionSections } from './questionSnapshot'

/**
 * Bucket the request into one of a few contexts so the confidentiality
 * banner can phrase itself correctly:
 *   - `campaign-line-manager`: upward feedback to your line manager
 *   - `campaign-cgm`: bulk CGM campaign; respondent picks CGM in Q1
 *   - `campaign-organisation`: organisation-wide survey (e.g. Culture
 *     Survey); no subject at all — the "About <X>" line is dropped and
 *     the header kicker reads "Organisation survey".
 *   - `in-the-moment`: per-request feedback (matrix / project / employee
 *     requested / peer). The subject name is a real person and we want
 *     to surface it in the banner.
 *
 * The dialog passes the bucket in; the form just renders the matching
 * banner. The form has no opinion on campaigns vs in-the-moment.
 */
export type ConfidentialityContext =
  | 'campaign-line-manager'
  | 'campaign-cgm'
  | 'campaign-organisation'
  | 'in-the-moment'

interface FeedbackResponseFormProps {
  requestId: string
  requestTitle: string
  requestedForUser: string
  questions: FeedbackQuestion[]
  isAnonymous?: boolean
  isConfidential?: boolean
  /** See ConfidentialityContext docs. Optional — defaults to in-the-moment. */
  confidentialityContext?: ConfidentialityContext
  /** Small heading above the title; defaults by context. */
  kicker?: string
  instructions?: string
  onSubmit: (responseData: QuestionResponse[]) => Promise<void>
  onCancel?: () => void
  loading?: boolean
}

export interface QuestionResponse {
  question_id: string
  question_text: string
  response_type: string
  answer: any
  rating?: number
  comment?: string
}

// ----------------------------------------------------------------------------
// Type rail — the only signal of question type. No badges, no icons.
// Subtle palette keeps the page calm; heritage-blue stays the dominant accent.
// ----------------------------------------------------------------------------
const TYPE_RAIL: Record<string, string> = {
  likert: 'bg-heritage-blue',
  rating: 'bg-[#C97B5C]', // warm terracotta
  rating_scale: 'bg-[#C97B5C]',
  scale: 'bg-[#C97B5C]',
  text: 'bg-[#D4C9A8]', // sand
  multiple_choice: 'bg-[#2B6770]', // deep teal
  multi_select: 'bg-[#2B6770]',
  yes_no: 'bg-slate-400',
}

function railFor(type: string): string {
  return TYPE_RAIL[type] || 'bg-slate-300'
}

// Two-digit "01 / 08" tabular index, small-caps tracked.
function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

// Draft auto-save. Long surveys (the Culture Survey is 70+ questions) were
// being lost wholesale when the dialog was dismissed mid-way — answers lived
// only in component state. Drafts persist per-request in localStorage on this
// device, restore on reopen, and clear only after a CONFIRMED submit.
const DRAFT_PREFIX = 'feedback-draft-'
const DRAFT_TTL_MS = 60 * 24 * 60 * 60 * 1000 // 60 days — outlives any campaign window

function readDraft(requestId: string): Record<string, QuestionResponse> | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + requestId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.responses || Date.now() - (parsed.savedAt || 0) > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_PREFIX + requestId)
      return null
    }
    return parsed.responses
  } catch {
    return null
  }
}

export default function FeedbackResponseForm({
  requestId,
  requestTitle,
  requestedForUser,
  questions,
  isAnonymous = false,
  isConfidential = false,
  confidentialityContext = 'in-the-moment',
  kicker,
  instructions,
  onSubmit,
  onCancel,
  loading = false,
}: FeedbackResponseFormProps) {
  // ----- sections (#13, Oct 2026) -----
  // A requester's final list is shown under a heading per question bank
  // category; older requests and campaigns stay one unheaded list. Counting,
  // numbering and "first missing question" all follow the order on screen.
  const sections = useMemo(() => questionSections(questions), [questions])
  const grouped = sections.some((s) => s.title !== null)
  const orderedQuestions = useMemo(() => sections.flatMap((s) => s.questions), [sections])
  const QuestionHeading = grouped ? 'h4' : 'h3'

  const [responses, setResponses] = useState<Record<string, QuestionResponse>>(
    () => readDraft(requestId) || {},
  )
  const [draftRestored] = useState(() => !!readDraft(requestId))
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Persist every change; storage failures (quota, private mode) are non-fatal.
  useEffect(() => {
    if (Object.keys(responses).length === 0) return
    try {
      localStorage.setItem(
        DRAFT_PREFIX + requestId,
        JSON.stringify({ savedAt: Date.now(), responses }),
      )
    } catch {
      /* draft persistence is best-effort */
    }
  }, [responses, requestId])

  const handleQuestionResponse = (questionId: string, question: FeedbackQuestion, answer: any) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        question_text: question.text,
        response_type: question.type,
        answer,
      },
    }))
    if (errors[questionId]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[questionId]
        return next
      })
    }
  }

  // Uses the same "answered" rule as the footer counter (isAnswered below) —
  // a mere `responses[q.id]` existence check passed answers that were typed
  // then cleared ('') or fully untoggled ([]), so the submit gate and the
  // "N required outstanding" counter could contradict each other.
  const validateForm = (): string[] => {
    const next: Record<string, string> = {}
    orderedQuestions.forEach((q) => {
      if (q.required && !isAnswered(q)) {
        next[q.id] = 'A response is required.'
      }
    })
    setErrors(next)
    return Object.keys(next)
  }

  // ----- finding unanswered questions on a long survey -----
  // 70+ question surveys made missed questions a scroll hunt: a failed submit
  // scrolls to the FIRST missing one, and the footer's outstanding counter is
  // a jump button cycling through the rest. The flash ring marks the landing.
  const questionRefs = useRef<Record<string, HTMLElement | null>>({})
  const jumpCursorRef = useRef(0)
  const [flashId, setFlashId] = useState<string | null>(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(flashTimerRef.current), [])

  const scrollToQuestion = (id: string) => {
    questionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setFlashId(id)
    clearTimeout(flashTimerRef.current)
    flashTimerRef.current = setTimeout(() => setFlashId(null), 1600)
  }

  const jumpToNextOutstanding = () => {
    if (outstandingRequired.length === 0) return
    const i = jumpCursorRef.current % outstandingRequired.length
    jumpCursorRef.current = i + 1
    scrollToQuestion(outstandingRequired[i].id)
  }

  const handleSubmit = async () => {
    const missing = validateForm()
    if (missing.length > 0) {
      scrollToQuestion(missing[0])
      return
    }
    try {
      await onSubmit(Object.values(responses))
      // Only a confirmed submit clears the draft — the dialog rethrows on
      // failure so a failed submit keeps the answers recoverable.
      try {
        localStorage.removeItem(DRAFT_PREFIX + requestId)
      } catch { /* best-effort */ }
    } catch {
      /* submit failed — parent already surfaced the error; draft stays */
    }
  }

  // ----- progress -----
  // Headline counter is "X of N answered" against the FULL question set so
  // respondents see real progress through what they're seeing, not against a
  // hidden subset of required ones. Required-completion is enforced at submit
  // (and surfaced as a sub-line below the main counter) — keeping the two
  // metrics distinct avoids the "2 of 13" confusion when 27 questions are
  // actually on screen.
  const isAnswered = (q: FeedbackQuestion): boolean => {
    const a = responses[q.id]?.answer
    return a !== undefined && a !== null && a !== '' && !(Array.isArray(a) && a.length === 0)
  }
  const requiredQuestions = useMemo(() => orderedQuestions.filter((q) => q.required), [orderedQuestions])
  // Live list (not just a count) — drives the footer's jump-to-next button.
  const outstandingRequired = useMemo(
    () => requiredQuestions.filter((q) => !isAnswered(q)),
    [requiredQuestions, responses],
  )
  const answeredTotal = useMemo(
    () => orderedQuestions.filter(isAnswered).length,
    [orderedQuestions, responses],
  )
  // Bar tracks total progress; submit is gated separately by validateForm().
  const progressPct = questions.length
    ? Math.round((answeredTotal / questions.length) * 100)
    : 100
  const requiredOutstanding = outstandingRequired.length

  // ----- One-time stagger on mount via CSS animation delay -----
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const t = window.requestAnimationFrame(() => setMounted(true))
    return () => window.cancelAnimationFrame(t)
  }, [])

  // ============================================================================
  // Per-question renderers
  // ============================================================================

  const renderQuestion = (question: FeedbackQuestion) => {
    const response = responses[question.id]

    switch (question.type) {
      // -------- Likert + multiple-choice (textual options) --------
      case 'likert':
      case 'multiple_choice': {
        const opts: string[] = (question as any).options || (question as any).config?.options || []
        return question.type === 'likert' ? (
          <SegmentedChoice
            options={opts}
            value={response?.answer}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
          />
        ) : (
          <StackedChoice
            options={opts}
            otherOptions={(question as any).config?.other_options || []}
            value={response?.answer}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
          />
        )
      }

      // -------- Rating / numeric scale --------
      case 'rating':
      case 'rating_scale':
      case 'scale': {
        const cfg = (question as any).scale || (question as any).config || { min: 1, max: 5 }
        const min = cfg.min ?? 1
        const max = cfg.max ?? 5
        const minLabel = cfg.min_label || 'Low'
        const maxLabel = cfg.max_label || 'High'
        const values = Array.from({ length: max - min + 1 }, (_, i) => min + i)
        return (
          <SegmentedNumeric
            values={values}
            minLabel={minLabel}
            maxLabel={maxLabel}
            value={response?.answer}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
          />
        )
      }

      // -------- Multi-select --------
      case 'multi_select': {
        const opts: string[] = (question as any).options || (question as any).config?.options || []
        const selected: string[] = response?.answer || []
        // question_config.max_selections caps how many options may be picked
        // (e.g. "your top 2 AI tools") — unselected options dim once the cap
        // is reached; deselecting frees a slot.
        const maxSelections: number | undefined = (question as any).config?.max_selections
        return (
          <StackedChoice
            options={opts}
            otherOptions={(question as any).config?.other_options || []}
            multiple
            maxSelections={maxSelections}
            value={selected}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
          />
        )
      }

      // -------- Yes / No --------
      case 'yes_no':
        return (
          <YesNo
            value={response?.answer}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
          />
        )

      // -------- Text (free-form) --------
      case 'text':
        return (
          <NotebookTextarea
            value={response?.answer || ''}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
            placeholder="Add your response"
            invalid={!!errors[question.id]}
          />
        )

      default:
        return (
          <NotebookTextarea
            value={response?.answer || ''}
            onChange={(v) => handleQuestionResponse(question.id, question, v)}
            placeholder="Add your response"
            invalid={!!errors[question.id]}
            rows={2}
          />
        )
    }
  }

  // One question card, numbered by its place in the order on screen.
  const renderQuestionCard = (question: FeedbackQuestion) => {
    const index = orderedQuestions.indexOf(question)
    return (
      <article
        key={question.id}
        ref={(el) => { questionRefs.current[question.id] = el }}
        className={[
          'relative rounded-md border bg-white pl-6 pr-7 py-7 sm:pl-8 sm:pr-9 sm:py-8 shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-all duration-700 ease-out',
          // A missed required question must be findable from three rooms
          // away on a 70-question page — full card treatment, not just
          // the small note under the control.
          errors[question.id]
            ? 'border-rose-300 bg-rose-50/40'
            : 'border-[#E7E5DB]',
          flashId === question.id ? 'ring-2 ring-amber-400/80 ring-offset-2' : '',
        ].join(' ')}
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(10px)',
          transitionDelay: `${180 + index * 60}ms`,
        }}
      >
        {/* Type rail (left edge, full-height, 3px wide) — rose when the
            question was missed on submit. */}
        <span
          aria-hidden
          className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-md ${errors[question.id] ? 'bg-rose-500' : railFor(question.type)}`}
        />

        {/* Index + question text */}
        <div className="flex items-start justify-between gap-6">
          <QuestionHeading className="flex-1 text-[20px] sm:text-[22px] font-light leading-snug tracking-tight text-slate-900">
            {question.text}
            {question.required && (
              <span
                className={`ml-1 align-top ${errors[question.id] ? 'text-rose-500' : 'text-slate-300'}`}
                aria-label="required"
              >
                *
              </span>
            )}
          </QuestionHeading>
          <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-[0.22em] tabular-nums text-slate-400">
            {pad2(index + 1)} <span className="text-slate-300">/</span> {pad2(questions.length)}
          </span>
        </div>

        {question.help_text && (
          <p className="mt-2 text-[14px] leading-relaxed text-slate-500">{question.help_text}</p>
        )}

        {/* Response control */}
        <div className="mt-6">{renderQuestion(question)}</div>

        {errors[question.id] && (
          <p className="mt-3 text-[13px] font-medium text-rose-600">{errors[question.id]}</p>
        )}
      </article>
    )
  }

  // ============================================================================
  // Layout
  // ============================================================================

  const showConfidential = isAnonymous || isConfidential

  return (
    <div className="bg-[#FAFAF7]">
      {/* Document column — letter-like spacing, 640px reading width */}
      <div className="mx-auto max-w-[820px] px-6 sm:px-12 pt-12 pb-36">
        {/* --------- Header --------- */}
        <header
          className="space-y-2 transition-all duration-700 ease-out"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(8px)',
          }}
        >
          <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-slate-400">
            {kicker ?? (confidentialityContext === 'campaign-organisation' ? 'Organisation survey' : 'Upward feedback')}
          </div>
          <h2 className="text-[28px] sm:text-[30px] font-light leading-tight tracking-tight text-slate-900">
            {requestTitle}
          </h2>
          {/* Organisation-wide surveys have no subject — an "About <X>" line
              would misframe the survey as being about a person. */}
          {confidentialityContext !== 'campaign-organisation' && (
            <p className="text-[15px] text-slate-600">
              About <span className="font-medium text-slate-800">{requestedForUser}</span>
            </p>
          )}
        </header>

        {/* --------- Confidentiality reassurance ---------
            Phrased like a sentence, not a compliance chip. Sits above the
            instructions so the respondent reads it before anything else.
            The exact wording depends on the request context — campaign
            (line manager / CGM) vs in-the-moment. */}
        {showConfidential && (
          <div
            className="mt-8 flex items-start gap-3 rounded-md border border-[#E7E5DB] bg-white px-4 py-3 transition-all duration-700 ease-out"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(8px)',
              transitionDelay: '60ms',
            }}
          >
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-heritage-blue" />
            <p className="text-[14px] leading-relaxed text-slate-700">
              {confidentialityContext === 'campaign-line-manager' && (
                <>
                  Your responses are confidential. They are not attributed to you —
                  your people leader only sees aggregated results.
                </>
              )}
              {confidentialityContext === 'campaign-cgm' && (
                <>
                  Your responses are confidential. They are not attributed to you —
                  the Campus General Manager you select only sees aggregated results.
                </>
              )}
              {confidentialityContext === 'campaign-organisation' && (
                <>
                  Your responses are anonymous. Individual answers are never
                  attributed to you — results are analysed in aggregate to guide
                  practical culture actions.
                </>
              )}
              {confidentialityContext === 'in-the-moment' && (
                isAnonymous ? (
                  <>
                    Your response is anonymous. Your name is not shared with{' '}
                    <span className="font-medium">{requestedForUser}</span>.
                  </>
                ) : (
                  <>
                    Your response is confidential and visible only to the person who
                    requested it and{' '}
                    <span className="font-medium">{requestedForUser}</span>.
                  </>
                )
              )}
            </p>
          </div>
        )}

        {/* --------- Instructions (optional, lighter than before) --------- */}
        {instructions && (
          <p
            className="mt-8 text-[15px] leading-relaxed text-slate-600 transition-all duration-700 ease-out"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(8px)',
              transitionDelay: '120ms',
            }}
          >
            {instructions}
          </p>
        )}

        {/* --------- Questions --------- */}
        <div className="mt-12 space-y-10">
          {grouped
            ? sections.map((section) => (
                <section key={section.key} className="space-y-6">
                  {section.title && (
                    <h3 className="border-b border-[#E7E5DB] pb-2 text-[12px] font-medium uppercase tracking-[0.22em] text-heritage-blue">
                      {section.title}
                    </h3>
                  )}
                  <div className="space-y-10">{section.questions.map(renderQuestionCard)}</div>
                </section>
              ))
            : orderedQuestions.map(renderQuestionCard)}
        </div>

      </div>

      {/* --------- Sticky submit footer --------- */}
      <div className="sticky bottom-0 left-0 right-0 -mt-px border-t border-[#E7E5DB] bg-[#FAFAF7]/95 backdrop-blur-sm">
        <div className="mx-auto max-w-[820px] px-6 sm:px-12 py-5 flex items-center gap-6">
          {/* Progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-[12px] font-medium uppercase tracking-[0.18em] text-slate-500">
                {answeredTotal} of {questions.length} answered
                {/* Reassure long-survey respondents their answers survive an
                    accidental close — drafts persist per-device (see readDraft). */}
                <span className="ml-2 normal-case tracking-normal font-normal text-slate-400">
                  · {draftRestored && answeredTotal > 0 ? 'draft restored — answers save automatically' : 'answers save automatically on this device'}
                </span>
              </div>
              {requiredQuestions.length > 0 && (
                requiredOutstanding === 0 ? (
                  <div className="text-[11px] tracking-[0.04em] text-slate-400 tabular-nums">
                    All required complete
                  </div>
                ) : (
                  /* Jump button — clicking cycles through the unanswered
                     required questions so nobody scroll-hunts for them.
                     Rose once a submit attempt has marked them. */
                  <button
                    type="button"
                    onClick={jumpToNextOutstanding}
                    className={[
                      'inline-flex items-center gap-1 text-[11px] tracking-[0.04em] tabular-nums transition-colors',
                      'underline decoration-dotted underline-offset-2',
                      Object.keys(errors).length > 0
                        ? 'text-rose-600 hover:text-rose-700'
                        : 'text-slate-500 hover:text-slate-700',
                    ].join(' ')}
                    title="Jump to the next unanswered required question"
                  >
                    {requiredOutstanding} required outstanding
                    <ChevronDown className="h-3 w-3" aria-hidden />
                  </button>
                )
              )}
            </div>
            <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-heritage-blue transition-[width] duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="h-[52px] px-7 bg-heritage-blue hover:bg-heritage-blue/90 text-[15px] font-medium tracking-tight"
          >
            {loading ? (
              <>Sharing…</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2.5" />
                Share your feedback
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Stepped Likert — circular nodes on a horizontal track, one per option,
 * with each option's full label sitting under its node.
 *
 * Think Apple-health pain scale or a stepper indicator. The track itself
 * is the visual cue; nodes scale with selection. Hovering a node previews
 * the label as the active one (italic). No echo line — the label under the
 * selected node carries the confirmation.
 */
function SegmentedChoice({
  options,
  value,
  onChange,
}: {
  options: string[]
  value?: string
  onChange: (v: string) => void
}) {
  const [hovered, setHovered] = useState<number | null>(null)
  if (options.length === 0) return null

  // Find indices of selected + hovered for track-fill calculation.
  const selectedIdx = options.findIndex((o) => o === value)
  const fillIdx = hovered !== null ? hovered : selectedIdx
  const fillPct = options.length === 1
    ? 0
    : Math.max(0, fillIdx) / (options.length - 1) * 100

  return (
    <div
      role="radiogroup"
      onMouseLeave={() => setHovered(null)}
      className="pt-2 pb-1"
    >
      {/* Track + nodes */}
      <div className="relative">
        {/* Faint baseline rule running through the node centres */}
        <div
          aria-hidden
          className="absolute left-[5%] right-[5%] top-1/2 -translate-y-1/2 h-[2px] rounded-full bg-slate-200"
        />
        {/* Heritage-blue progressed segment of the track */}
        <div
          aria-hidden
          className="absolute left-[5%] top-1/2 -translate-y-1/2 h-[2px] rounded-full bg-heritage-blue/80 transition-[width] duration-300 ease-out"
          style={{
            width:
              fillIdx >= 0
                ? `calc((100% - 10%) * ${fillIdx / Math.max(1, options.length - 1)})`
                : 0,
            opacity: selectedIdx >= 0 || hovered !== null ? 1 : 0,
          }}
        />

        <div className="relative flex justify-between">
          {options.map((opt, i) => {
            const isSelected = i === selectedIdx
            const isFillReached = i <= fillIdx && fillIdx >= 0
            const isHovered = hovered === i
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={opt}
                onClick={() => onChange(opt)}
                onMouseEnter={() => setHovered(i)}
                onFocus={() => setHovered(i)}
                onBlur={() => setHovered(null)}
                className={[
                  'relative z-10 h-12 w-12 rounded-full transition-all duration-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                  'flex items-center justify-center',
                ].join(' ')}
                title={opt}
              >
                {/* Outer ring */}
                <span
                  className={[
                    'block h-9 w-9 rounded-full transition-all duration-200',
                    'flex items-center justify-center',
                    isSelected
                      ? 'bg-heritage-blue ring-[6px] ring-heritage-blue/15 scale-110'
                      : isFillReached
                      ? 'bg-white border-2 border-heritage-blue'
                      : isHovered
                      ? 'bg-white border-2 border-slate-400 scale-105'
                      : 'bg-white border-2 border-slate-300',
                  ].join(' ')}
                >
                  {/* Inner dot — only on selected to feel anchored */}
                  {isSelected && (
                    <span className="block h-2 w-2 rounded-full bg-white" />
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Option labels — one under each node, full text */}
      <div className="mt-3 grid" style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}>
        {options.map((opt, i) => {
          const isSelected = i === selectedIdx
          const isHovered = hovered === i && !isSelected
          return (
            <button
              key={`label-${opt}`}
              type="button"
              tabIndex={-1}
              onClick={() => onChange(opt)}
              onMouseEnter={() => setHovered(i)}
              className={[
                'text-center px-1 transition-colors duration-200',
                'text-[12px] leading-snug',
                isSelected
                  ? 'text-heritage-blue font-medium'
                  : isHovered
                  ? 'italic text-slate-700'
                  : 'text-slate-500',
              ].join(' ')}
            >
              {opt}
            </button>
          )
        })}
      </div>

      {/* Track-fill bar indicator placeholder kept transparent so nothing visible adds height */}
      <div className="h-1" />
    </div>
  )
}

/**
 * Segmented numeric (rating / scale) — same pill pattern with numbers.
 *
 * Renders each value as a tabular-nums label inside the segment so wider
 * scales (1–10) stay legible.
 */
function SegmentedNumeric({
  values,
  minLabel,
  maxLabel,
  value,
  onChange,
}: {
  values: number[]
  minLabel?: string
  maxLabel?: string
  value?: number | string
  onChange: (v: number) => void
}) {
  const sel = typeof value === 'string' ? Number(value) : value
  return (
    <div>
      <div
        className="flex w-full overflow-hidden rounded-md border border-slate-200 bg-slate-50"
        role="radiogroup"
      >
        {values.map((v, i) => {
          const isSelected = sel === v
          const isFirst = i === 0
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={String(v)}
              onClick={() => onChange(v)}
              className={[
                'flex-1 h-12 text-[14px] tabular-nums font-medium transition-all duration-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/40 focus-visible:z-10',
                isSelected
                  ? 'bg-heritage-blue text-white'
                  : 'bg-transparent text-slate-600 hover:bg-white',
                !isFirst ? 'border-l border-slate-200' : '',
              ].join(' ')}
            >
              {v}
            </button>
          )
        })}
      </div>
      {(minLabel || maxLabel) && (
        <div className="mt-2 flex justify-between text-[12px] text-slate-500">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Stacked choice (multiple-choice + multi-select).
 *
 * Full-width rectangles; checkbox-style or radio-style depending on the
 * `multiple` flag. Selected state uses heritage-blue with a tick on the
 * leading edge — no round dots, which fight the editorial typography.
 *
 * `otherOptions` (question_config.other_options) lists option labels that
 * reveal an inline "please specify" input when selected — e.g. "Other" or
 * "Prefer to self-describe". The write-in composes into the stored answer
 * as `"<Option>: <text>"` so the response payload and exports keep their
 * plain-string shape; clearing the text falls back to the bare option.
 */
function StackedChoice({
  options,
  value,
  multiple = false,
  otherOptions = [],
  maxSelections,
  onChange,
}: {
  options: string[]
  value?: string | string[]
  multiple?: boolean
  otherOptions?: string[]
  /** Cap on picks in multiple mode (question_config.max_selections). */
  maxSelections?: number
  onChange: (v: any) => void
}) {
  // An option with a write-in is stored as "<opt>" or "<opt>: <text>".
  const matches = (val: unknown, opt: string): boolean =>
    typeof val === 'string' && (val === opt || val.startsWith(opt + ': '))
  const textOf = (val: unknown, opt: string): string =>
    typeof val === 'string' && val.startsWith(opt + ': ') ? val.slice(opt.length + 2) : ''
  const compose = (opt: string, text: string): string =>
    text.trim() ? `${opt}: ${text}` : opt

  const current: string[] = multiple
    ? (Array.isArray(value) ? value : [])
    : (typeof value === 'string' && value ? [value] : [])

  const isSelected = (opt: string): boolean => current.some((v) => matches(v, opt))
  const atMax = multiple && !!maxSelections && current.length >= maxSelections

  const toggle = (opt: string) => {
    if (multiple) {
      if (isSelected(opt)) {
        onChange(current.filter((v) => !matches(v, opt)))
      } else if (!atMax) {
        onChange([...current, opt])
      }
      // At the cap, clicks on unselected options are ignored (they render
      // dimmed) — deselect something first.
    } else {
      onChange(opt)
    }
  }

  const setOtherText = (opt: string, text: string) => {
    if (multiple) {
      onChange(current.map((v) => (matches(v, opt) ? compose(opt, text) : v)))
    } else {
      onChange(compose(opt, text))
    }
  }

  return (
    <div className="space-y-2">
      {multiple && !!maxSelections && (
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400 tabular-nums">
          {current.length} of {maxSelections} selected
        </div>
      )}
      {options.map((opt) => {
        const sel = isSelected(opt)
        const hasWriteIn = otherOptions.includes(opt)
        const writeInValue = sel
          ? textOf(current.find((v) => matches(v, opt)), opt)
          : ''
        const capped = atMax && !sel
        return (
          <div key={opt}>
            <button
              type="button"
              onClick={() => toggle(opt)}
              aria-pressed={sel}
              aria-disabled={capped}
              className={[
                'group flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left transition-all duration-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/40',
                sel
                  ? 'border-heritage-blue bg-heritage-blue/[0.04] text-slate-900'
                  : capped
                  ? 'border-slate-200 bg-white text-slate-400 opacity-60 cursor-not-allowed'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50',
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-5 w-5 flex-shrink-0 items-center justify-center transition-all duration-200',
                  multiple ? 'rounded-[3px]' : 'rounded-[3px]',
                  sel
                    ? 'bg-heritage-blue text-white'
                    : 'bg-white text-transparent border border-slate-300 group-hover:border-slate-400',
                ].join(' ')}
              >
                <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden>
                  <path
                    d="M3 8.5l3 3 7-7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="text-[15px] leading-snug">{opt}</span>
            </button>
            {/* Inline write-in — rendered OUTSIDE the button (nesting an
                input in a button both is invalid HTML and would toggle the
                option on every click into the field). Indented to read as
                belonging to the option above. */}
            {hasWriteIn && sel && (
              <div className="mt-1.5 ml-8">
                <input
                  type="text"
                  value={writeInValue}
                  onChange={(e) => setOtherText(opt, e.target.value)}
                  placeholder="Please specify…"
                  autoFocus={!writeInValue}
                  className={[
                    'w-full rounded-md border border-[#E7E5DB] bg-[#FCFBF6] px-3 py-2',
                    'text-[14px] text-slate-900 placeholder:italic placeholder:text-slate-400',
                    'focus:outline-none focus:border-heritage-blue/40 focus:bg-[#FBFAF4]',
                    'transition-colors duration-200',
                  ].join(' ')}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Yes / No — two large pills side-by-side. Neutral colours; the choice
 * itself carries the meaning, not red-vs-green which would feel like a quiz.
 */
function YesNo({
  value,
  onChange,
}: {
  value?: string
  onChange: (v: 'Yes' | 'No') => void
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {(['Yes', 'No'] as const).map((opt) => {
        const sel = value === opt
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={[
              'h-[60px] rounded-md border text-[15px] font-medium transition-all duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/40',
              sel
                ? 'border-heritage-blue bg-heritage-blue text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-800',
            ].join(' ')}
          >
            {opt}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Notebook-paper textarea — framed in a sand-bordered card with subtle
 * ruled lines and a focus accent rail on the left edge. The placeholder
 * sits italic at top; on focus the left rail lights up in heritage-blue
 * and a faint sand background warms the surface. A word counter sits in
 * the bottom-right of the frame.
 *
 * The ruled lines use a 28px linear-gradient (matching the line-height of
 * the text) so the writing always rests on a rule rather than floating.
 */
function NotebookTextarea({
  value,
  onChange,
  placeholder,
  rows = 5,
  invalid = false,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
  invalid?: boolean
}) {
  const [focused, setFocused] = useState(false)
  const wordCount = useMemo(
    () => (value.trim() ? value.trim().split(/\s+/).length : 0),
    [value],
  )

  return (
    <div
      className={[
        'group relative overflow-hidden rounded-md transition-all duration-300',
        invalid
          ? 'border border-rose-300 bg-rose-50/30'
          : focused
          ? 'border border-heritage-blue/30 bg-[#FBFAF4] shadow-[0_1px_0_rgba(0,62,95,0.04)]'
          : 'border border-[#E7E5DB] bg-[#FCFBF6]',
      ].join(' ')}
    >
      {/* Left accent rail — sand at rest, heritage-blue on focus */}
      <span
        aria-hidden
        className={[
          'absolute left-0 top-0 bottom-0 w-[3px] transition-colors duration-300',
          invalid
            ? 'bg-rose-400'
            : focused
            ? 'bg-heritage-blue'
            : 'bg-[#D4C9A8]',
        ].join(' ')}
      />

      {/* Ruled-line paper texture, behind the text */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.55]"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, transparent 0, transparent 27px, #E7E5DB 27px, #E7E5DB 28px)',
          backgroundSize: '100% 28px',
          backgroundPosition: '0 8px',
        }}
      />

      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        rows={rows}
        className={[
          'relative resize-none rounded-none border-0 bg-transparent',
          'pl-6 pr-5 pt-2 pb-9',
          'text-[15px] leading-[28px] text-slate-900',
          'placeholder:italic placeholder:text-slate-400',
          'focus-visible:ring-0 focus-visible:ring-offset-0',
          'shadow-none',
        ].join(' ')}
      />

      {/* Bottom-right word counter — only when there's something written */}
      {wordCount > 0 && (
        <div className="pointer-events-none absolute bottom-2.5 right-4 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400 tabular-nums">
          {wordCount} word{wordCount === 1 ? '' : 's'}
        </div>
      )}
    </div>
  )
}
